/**
 * Small fetch wrapper for the Tracking Links & Attribution admin API.
 * Same admin JWT/login as the Insights CMS — shares the same token key
 * so signing into either admin panel signs you into both.
 */
const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "insights_admin_token";

export function getToken() {
  return typeof window !== "undefined" ? window.localStorage.getItem(TOKEN_KEY) : null;
}
export function setToken(t) {
  if (t) window.localStorage.setItem(TOKEN_KEY, t);
  else window.localStorage.removeItem(TOKEN_KEY);
}

async function request(path, { method = "GET", body, headers: extraHeaders } = {}) {
  const headers = { "Content-Type": "application/json", ...extraHeaders };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.detail;
    const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((e) => e.msg || JSON.stringify(e)).join(", ") : `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const marketingApi = {
  login: (email, password) => request("/api/auth/login", { method: "POST", body: { email, password } }),
  me: () => request("/api/auth/me"),
  listLinks: (filters = {}) => {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    return request(`/api/admin/tracking-links${q ? `?${q}` : ""}`);
  },
  getLink: (linkId) => request(`/api/admin/tracking-links/${linkId}`),
  createLink: (payload) => request("/api/admin/tracking-links", { method: "POST", body: payload }),
  updateLink: (linkId, payload) => request(`/api/admin/tracking-links/${linkId}`, { method: "PATCH", body: payload }),
  duplicateLink: (linkId, name) => request(`/api/admin/tracking-links/${linkId}/duplicate`, { method: "POST", body: { name: name || null } }),
  deactivateLink: (linkId) => request(`/api/admin/tracking-links/${linkId}/deactivate`, { method: "POST" }),
  reactivateLink: (linkId) => request(`/api/admin/tracking-links/${linkId}/reactivate`, { method: "POST" }),
  generateQr: (linkId, trackSeparately) => request(`/api/admin/tracking-links/${linkId}/qr`, { method: "POST", body: { track_separately: !!trackSeparately } }),
  getPaidAds: (linkId, platform) => request(`/api/admin/tracking-links/${linkId}/paid-ads?platform=${platform}`),
  getSettings: () => request("/api/admin/tracking-links/settings"),
  batchCreate: (payload) => request("/api/admin/tracking-links/batch", { method: "POST", body: payload }),
  getLinkPerformance: (linkId, days = 30) => request(`/api/admin/tracking-links/${linkId}/performance?days=${days}`),
  getAnalyticsOverview: (filters = {}) => {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    return request(`/api/admin/tracking-links/analytics/overview${q ? `?${q}` : ""}`);
  },
};
